import React, { useEffect, useMemo, useState } from 'react';
import PageHeader from '../PageHeader';
import SmartImage from '../ui/SmartImage';
import MediaSkeleton from '../ui/MediaSkeleton';

const TYPE_LABELS = {
    annual_report: 'Annual Report',
    sustainability_report: 'Sustainability Report',
    financial_report: 'Financial Report',
    company_profile: 'Company Profile',
};

function PublicationSection({ type = null }) {
    const [publications, setPublications] = useState([]);
    const [selectedYear, setSelectedYear] = useState('all');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const controller = new AbortController();
        const params = new URLSearchParams();
        if (type) params.set('type', type);
        const query = params.toString();
        const endpoint = `${process.env.REACT_APP_BACKEND_URL}/publications${query ? `?${query}` : ''}`;

        setLoading(true);
        setError('');
        fetch(endpoint, { signal: controller.signal })
            .then((response) => {
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                return response.json();
            })
            .then((data) => setPublications(Array.isArray(data.data) ? data.data : []))
            .catch((fetchError) => {
                if (fetchError.name !== 'AbortError') {
                    console.error('Error fetching publications:', fetchError);
                    setError('Publikasi belum dapat dimuat. Silakan coba kembali.');
                }
            })
            .finally(() => setLoading(false));

        return () => controller.abort();
    }, [type]);

    const years = useMemo(() => [...new Set(publications.map((item) => item.year).filter(Boolean))].sort((a, b) => Number(b) - Number(a)), [publications]);
    const filtered = useMemo(() => selectedYear === 'all' ? publications : publications.filter((item) => String(item.year) === selectedYear), [publications, selectedYear]);
    const pageTitle = type && TYPE_LABELS[type] ? TYPE_LABELS[type] : 'Publikasi';

    return (
        <>
            <PageHeader title="Publikasi" />
            <main className="min-h-screen bg-slate-950 py-12 sm:py-16">
                <div className="container mx-auto max-w-6xl px-4 sm:px-6">
                    <div className="mb-10 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
                        <div>
                            <span className="text-xs font-semibold uppercase tracking-[0.24em] text-blue-400">Publikasi Perusahaan</span>
                            <h1 className="mt-2 text-3xl font-bold text-white sm:text-4xl">{pageTitle}</h1>
                            <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-400 sm:text-base">Akses dokumen publikasi resmi PT Len Telekomunikasi Indonesia berdasarkan tahun penerbitan.</p>
                        </div>
                        <div className="w-full md:w-52">
                            <label htmlFor="publication-year" className="mb-2 block text-[10px] font-semibold uppercase tracking-wider text-slate-400">Tahun</label>
                            <select id="publication-year" value={selectedYear} onChange={(event) => setSelectedYear(event.target.value)} className="w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-sm text-white outline-none transition focus:border-blue-500">
                                <option value="all">Semua Tahun</option>
                                {years.map((year) => <option key={year} value={String(year)}>{year}</option>)}
                            </select>
                        </div>
                    </div>

                    {loading && <MediaSkeleton count={3} compact />}
                    {!loading && error && <div className="rounded-2xl border border-red-900/60 bg-red-950/30 p-8 text-center text-red-200">{error}</div>}
                    {!loading && !error && filtered.length === 0 && <div className="rounded-2xl border border-slate-800 bg-slate-900 p-10 text-center text-slate-400">Belum ada publikasi yang tersedia untuk pilihan ini.</div>}

                    {!loading && !error && filtered.length > 0 && (
                        <div className="grid grid-cols-1 gap-7 sm:grid-cols-2 lg:grid-cols-3">
                            {filtered.map((publication) => (
                                <article key={publication.id} className="group overflow-hidden rounded-[26px] border border-slate-800 bg-slate-900 shadow-2xl shadow-black/10 transition hover:-translate-y-1 hover:border-slate-700">
                                    <SmartImage
                                        src={publication.cover_url}
                                        alt={publication.title}
                                        wrapperClassName="aspect-[3/4] w-full bg-slate-800"
                                        className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.02]"
                                        fallback={<div className="flex aspect-[3/4] items-center justify-center bg-slate-800 px-6 text-center text-sm text-slate-500">Cover belum tersedia</div>}
                                    />
                                    <div className="p-6">
                                        <div className="mb-3 flex items-center justify-between gap-3">
                                            <span className="rounded-full bg-blue-500/10 px-3 py-1 text-xs font-semibold text-blue-300">{TYPE_LABELS[publication.type] || publication.type || 'Publikasi'}</span>
                                            <span className="text-sm font-semibold text-slate-400">{publication.year || '-'}</span>
                                        </div>
                                        <h2 className="text-lg font-bold leading-snug text-white">{publication.title}</h2>
                                        {publication.description && <p className="mt-3 line-clamp-3 text-sm leading-6 text-slate-400">{publication.description}</p>}
                                        <div className="mt-6 flex flex-wrap gap-2">
                                            {publication.file_url ? (
                                                <>
                                                    <a href={publication.file_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-blue-500">Lihat laporan <span aria-hidden="true">&rarr;</span></a>
                                                    <a href={publication.file_url} target="_blank" rel="noopener noreferrer" download className="inline-flex items-center rounded-full border border-slate-700 px-4 py-2 text-xs font-semibold text-slate-300 transition hover:border-slate-500 hover:text-white">Unduh PDF</a>
                                                </>
                                            ) : <span className="text-sm text-slate-500">File PDF belum tersedia.</span>}
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>
                    )}
                </div>
            </main>
        </>
    );
}

export default PublicationSection;
